import { ShoppingPreferences, ShoppingPrefsPayload, SyncResult, StorageSource } from '@lib/shopping-preferences/types';
import { storage } from '@helpers/shopping-preferences/storage';
import { createDefaultPreferences, nowISO } from '@helpers/shopping-preferences/defaults';
import { validatePreferences } from '@helpers/shopping-preferences/validation';
import logger from '@helpers/utils/logger/client';

type SourceName = 'temporary' | 'permanent' | 'backend';

interface TimestampSource {
  prefs: ShoppingPreferences;
  source: SourceName;
}

const normalizeBackendPrefs = (backendPrefs: ShoppingPrefsPayload | null): ShoppingPreferences | null => {
  if (!backendPrefs) {
    return null;
  }

  return {
    universe: backendPrefs.universe,
    categories: backendPrefs.categories ?? [],
    custom: backendPrefs.custom ?? [],
    last_updated: backendPrefs.updated_at || nowISO(),
  };
};

const pickLatestByTimestamp = (sources: TimestampSource[]): TimestampSource | null => {
  if (!sources.length) {
    return null;
  }

  const priority: Record<SourceName, number> = {
    temporary: 0,
    permanent: 1,
    backend: 2,
  };

  const sortedSources = [...sources].sort((a, b) => {
    const timeA = new Date(a.prefs.last_updated).getTime();
    const timeB = new Date(b.prefs.last_updated).getTime();

    if (timeB !== timeA) {
      return timeB - timeA;
    }

    return priority[a.source] - priority[b.source];
  });

  return sortedSources[0];
};

export const syncPreferencesV2 = (
  backendPrefs: ShoppingPrefsPayload | null,
  clearTemporaryOnSync: boolean = false,
): SyncResult => {
  try {
    if (clearTemporaryOnSync && storage.hasTemporary()) {
      logger.info('[Shopping Prefs Sync v2] Clearing temporary storage on login');
      storage.clear('temporary');
    }

    const tempPrefs = storage.getTemporary();
    const permPrefs = storage.getPermanent();
    const backendNormalized = normalizeBackendPrefs(backendPrefs);

    const permanentSources: TimestampSource[] = [];

    if (permPrefs) {
      permanentSources.push({ prefs: permPrefs, source: 'permanent' });
    }
    if (backendNormalized) {
      permanentSources.push({ prefs: backendNormalized, source: 'backend' });
    }

    const hasRealPermanentSource = permanentSources.length > 0;

    const initialPermanentWinner =
      pickLatestByTimestamp(permanentSources) ??
      ({
        prefs: createDefaultPreferences(),
        source: 'permanent',
      } as TimestampSource);

    const permanentValidation = validatePreferences(initialPermanentWinner.prefs);

    const permanentWinner: TimestampSource = permanentValidation.isValid
      ? initialPermanentWinner
      : ({
          prefs: createDefaultPreferences(),
          source: 'permanent',
        } as TimestampSource);

    const permanentIsDefault = !hasRealPermanentSource || !permanentValidation.isValid;

    // Compute display (session) winner.
    // Important: when permanent winner is just "defaults" (no real prefs),
    // a valid temporary preference should always win for display, regardless of timestamps.
    const displaySources: TimestampSource[] = [{ prefs: permanentWinner.prefs, source: 'permanent' }];

    if (tempPrefs) {
      displaySources.push({ prefs: tempPrefs, source: 'temporary' });
    }

    const displayWinner: TimestampSource =
      tempPrefs && permanentIsDefault
        ? ({ prefs: tempPrefs, source: 'temporary' } as TimestampSource)
        : pickLatestByTimestamp(displaySources) ?? permanentWinner;

    let source: StorageSource = 'default';
    if (permanentWinner.source === 'permanent') {
      source = 'cookie';
    } else if (permanentWinner.source === 'backend') {
      source = 'backend';
    }

    logger.info('[Shopping Prefs Sync v2] Timestamp-based sync complete', {
      source,
      permanentTimestamp: permanentWinner.prefs.last_updated,
      permanentUniverse: permanentWinner.prefs.universe,
      displayTimestamp: displayWinner.prefs.last_updated,
      displayUniverse: displayWinner.prefs.universe,
    });

    return {
      success: true,
      source,
      preferences: permanentWinner.prefs,
      sessionPrefs: displayWinner.prefs,
    };
  } catch (error) {
    logger.error('[Shopping Prefs Sync v2] Error during sync:', error);
    return {
      success: false,
      source: null,
      preferences: createDefaultPreferences(),
      sessionPrefs: null,
      error: error as Error,
    };
  }
};
